import { Injectable } from '@angular/core'
import { Actions, Effect, ofType } from '@ngrx/effects'
import { Action, select, Store } from '@ngrx/store'

import { Observable, of } from 'rxjs'
import { catchError, map, switchMap, withLatestFrom, filter } from 'rxjs/operators'
import { withPredicateFrom } from '../../core/helpers/rx-pipes';

import { FacetService } from '../services/facet-service'

import * as facet from '../actions/facet'
import * as product from '../actions/product'
import * as status from '../../base/actions/status.actions'

import * as fromBase from '../../base/reducers'
import * as fromCatalog from '../reducers'
import * as fromUser from '../../auth/reducers/'

@Injectable()
export class FacetEffects {
  constructor(
    private actions$: Actions,
    private store: Store<fromCatalog.State>,
    private facetSvc: FacetService
  ) { }

  @Effect()
  initFacetGroups$: Observable<Action> =
    this.actions$.pipe(
      ofType<facet.InitGroups>(facet.INIT_GROUPS),
      withLatestFrom(
        this.store.pipe(select(fromCatalog.isFacetGroupsTtlExpired)),
        (action, isFacetGroupsTtlExpired) => ({ forceRefresh: action.forceRefresh, isFacetGroupsTtlExpired: isFacetGroupsTtlExpired })
      ),
      switchMap((data) => {
        let actionsArr = []
        if (data.forceRefresh || data.isFacetGroupsTtlExpired) {
          actionsArr.push(new facet.GetGroups())
        } else {
          actionsArr.push(new facet.GetGroupsSkipped())
        }
        return of(...actionsArr)
      })
    )

  @Effect()
  getFacetGroups$: Observable<Action> =
    this.actions$
      .pipe(
        ofType<facet.GetGroups>(facet.GET_GROUPS),
        switchMap(action =>
          this.facetSvc.getFacetGroups()
            .pipe(
              map(groups => new facet.GetGroupsSuccess(groups)),
              catchError(e => of(new facet.GetGroupsFailed(e)))
            )
        )
      )

  @Effect()
  initFacets$: Observable<Action> =
    this.actions$.pipe(
      ofType<facet.InitFacets>(facet.INIT_FACETS),
      withLatestFrom(
        this.store.pipe(select(fromCatalog.isFacetsTtlExpired)),
        (action, isFacetsTtlExpired) => ({ forceRefresh: action.forceRefresh, isFacetsTtlExpired: isFacetsTtlExpired })
      ),
      switchMap((data) => {
        let actionsArr = []
        if (data.forceRefresh || data.isFacetsTtlExpired) {
          actionsArr.push(new facet.GetFacets())
        } else {
          actionsArr.push(new facet.GetFacetsSkipped())
        }
        return of(...actionsArr)
      })

    )

  @Effect()
  getFacets$: Observable<Action> =
    this.actions$
      .pipe(
        ofType<facet.GetFacets>(facet.GET_FACETS),
        withLatestFrom(
          this.store.pipe(select(fromUser.getCustomerId)),
          (action, customerId) => ({ customerId })
        ),
        switchMap(data =>
          this.facetSvc.getFacetBaseline(data.customerId)
            .pipe(
              map(baseline => new facet.GetFacetsSuccess(baseline)),
              catchError(e => of(new facet.GetFacetsFailed(e)))
            )
        )
      )

  @Effect()
  initCompleted$: Observable<Action> =
    this.actions$
      .pipe(
        ofType<status.ApplySuccess>(status.APPLYSUCCESS),
        withLatestFrom(
          this.store.pipe(select(fromBase.loadProducts)),
          (action, loadProducts) => loadProducts
        ),
        filter(loadProducts => loadProducts),
        switchMap(() => of(new facet.Filter(), new product.Filter(), new product.GetFavorites()))
      )

  @Effect()
  filterFacets$: Observable<Action> =
    this.actions$
      .pipe(
        ofType<facet.Filter>(facet.FILTER),
        withLatestFrom(
          this.store.pipe(select(fromCatalog.getCombinedFilterState)),
          this.store.pipe(select(fromUser.getCustomerId)),
          this.store.pipe(select(fromBase.getPageParams)),
          this.store.pipe(select(fromCatalog.getOnlyInStock)),
          this.store.pipe(select(fromCatalog.getSearchTerm)),
          this.store.pipe(select(fromCatalog.getProductFilters)),
          (action, state, customerId, params, onlyInStock, searchTerm, filterItems) => ({state, customerId, params, onlyInStock, searchTerm, filterItems})
        ),
        switchMap(data => {
          return this.facetSvc.getFacetCount(
            data.state.pageId,
            data.customerId,
            data.state.facets,
            data.params,
            data.onlyInStock,
            data.searchTerm,
            data.filterItems)
            .pipe(
              map(facets => new facet.FilterSuccess(facets)),
              catchError(e => of(new facet.FilterFailed(e)))
            )
        })
      )
}
